import React, { useState, useEffect } from 'react';
import { Save, Check, CircleMinus, CircleCheck, Lock, Edit2 } from 'lucide-react';
import { OrgFunction } from './FunctionTypes';
import Modal from '../common/Modal';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea } from '../../validation/ValidatedFields';
import { isUniqueLocal } from '../../validation/uniqueness';
import { useTreeCycleGuard } from '../../validation/useTreeCycleGuard';

interface FunctionFormProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: Omit<OrgFunction, 'id'>) => Promise<void>;
    initialData?: OrgFunction | null;
    functions: OrgFunction[];
    /**
     * Approved + active departments only, for the "Falls Under" picker.
     * Kept separate from `functions` (the full list used by the cycle-guard)
     * so drafts / pending / rejected departments never appear as selectable
     * parents.
     */
    parentOptions: Array<{ id: number | string; name: string }>;
    onToggleActive?: () => Promise<void>;
    canModify?: boolean;
    isApproved?: boolean;
}

const FunctionForm: React.FC<FunctionFormProps> = ({ isOpen, onClose, onSubmit, initialData, functions, parentOptions, onToggleActive, canModify = true, isApproved = false }) => {
    const fieldVh = useFieldValidation();
    const [formData, setFormData] = useState({
        name: '',
        parentId: 'root',
        description: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [toggling, setToggling] = useState(false);
    // View-first edit: existing records open read-only until "Edit" is clicked.
    const [isEditing, setIsEditing] = useState(false);

    const fieldsDisabled = !!initialData && (!canModify || isApproved || !isEditing);

    const { forbiddenIds, cycleError } = useTreeCycleGuard(functions, initialData?.id ?? null, { idKey: 'id', parentKey: 'parentId' });

    useEffect(() => {
        if (initialData) {
            setFormData({
                name: initialData.name,
                parentId: initialData.parentId || 'root',
                description: initialData.description || ''
            });
        } else {
            setFormData({
                name: '',
                parentId: 'root',
                description: ''
            });
        }
        fieldVh.reset();
        setSubmitting(false);
        setToggling(false);
        setIsEditing(false);   // every open starts read-only ("Viewing")
    }, [initialData, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    const runDomainChecks = (): boolean => {
        let ok = true;
        const name = formData.name.trim();
        if (name && !isUniqueLocal(902, name, functions, initialData?.id)) {
            fieldVh.setError('name', 'A department with this name already exists in this organisation.'); ok = false;
        }
        const pid = formData.parentId;
        if (pid && pid !== 'root') {
            const err = cycleError(pid, { self: 'A department cannot be its own parent.', descendant: 'Cannot select a descendant — that would create a hierarchy cycle.' });
            if (err) { fieldVh.setError('parentId', err); ok = false; }
        }
        return ok;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const engineOk = fieldVh.validateAll([
            { name: 'name', code: 109, value: formData.name, required: true },
            { name: 'description', code: 110, value: formData.description },
        ]);
        const domainOk = runDomainChecks();
        if (!engineOk || !domainOk) return;
        setSubmitting(true);
        try {
            await onSubmit({ ...formData, name: formData.name.trim() });
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggle = async () => {
        if (!onToggleActive) return;
        setToggling(true);
        try {
            await onToggleActive();
        } finally {
            setToggling(false);
        }
    };

    const headerText = initialData
        ? isApproved ? <>Viewing (locked) "<span className="item-highlight">{initialData.name}</span>"</>
        : (canModify && isEditing) ? <>Editing "<span className="item-highlight">{initialData.name}</span>"</>
        : <>Viewing "<span className="item-highlight">{initialData.name}</span>"</>
        : 'Create Department';

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md" data-testid="MOD_function_form">
            <Modal.Header>{headerText}</Modal.Header>
            <Modal.Body>
                {isApproved && (
                    <div className="approved-banner">
                        <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
                    </div>
                )}
                <form
                    id="function-form"
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-y-4"
                >
                    {/* System code (DEP-N) — auto-generated on approve, read-only.
                        Only shown when editing; prefilled with the same code the list shows. */}
                    {initialData && (
                        <FormControl.Input
                            label="Code"
                            value={initialData.code || '—'}
                            disabled
                            data-testid="INP_function_code"
                        />
                    )}
                    <ValidatedInput
                        vh={fieldVh}
                        name="name"
                        code={109}
                        required
                        label="Department Name"
                        type="text"
                        value={formData.name}
                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g. Procurement"
                        disabled={fieldsDisabled}
                        data-testid="INP_function_name"
                    />

                    <FormControl.SearchSelect
                        label="Falls Under"
                        value={formData.parentId}
                        onChange={(value) => {
                            setFormData({ ...formData, parentId: value ? String(value) : 'root' });
                            fieldVh.clearError('parentId');
                        }}
                        options={parentOptions
                            .filter(f => !forbiddenIds.has(String(f.id)))
                            .map(f => ({ value: String(f.id), label: f.name }))}
                        placeholder="-- None (Top Level) --"
                        clearable
                        error={fieldVh.errors.parentId}
                        disabled={fieldsDisabled}
                        data-testid="SEL_function_parent"
                    />

                    <ValidatedTextarea
                        vh={fieldVh}
                        name="description"
                        code={110}
                        label="Description (Optional)"
                        rows={3}
                        value={formData.description}
                        onChange={e => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Brief description of department..."
                        disabled={fieldsDisabled}
                        data-testid="INP_function_description"
                    />
                </form>
            </Modal.Body>
            <Modal.Footer className={initialData && onToggleActive ? 'app-modal-footer--split' : ''}>
                {initialData && onToggleActive && !isApproved && (
                    <Button
                        variant={initialData.is_active ? 'inactive-outline' : 'active-outline'}
                        icon={initialData.is_active ? CircleMinus : CircleCheck}
                        onClick={handleToggle}
                        disabled={toggling || submitting}
                        data-testid="BTN_function_toggle_active"
                    >
                        {toggling ? '...' : initialData.is_active ? 'Inactive' : 'Active'}
                    </Button>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                    {/* View → Edit: existing record, read-only (brand color) */}
                    {initialData && canModify && !isApproved && !isEditing && (
                        <Button
                            variant="fill"
                            icon={Edit2}
                            onClick={() => setIsEditing(true)}
                            data-testid="BTN_function_edit"
                        >
                            Edit
                        </Button>
                    )}
                    {/* Submit (create) or Update (existing + editing) */}
                    {!isApproved && (!initialData || (canModify && isEditing)) && (
                        <Button
                            type="submit"
                            form="function-form"
                            variant={initialData ? 'update' : 'fill'}
                            icon={initialData ? Check : Save}
                            disabled={submitting || toggling || fieldsDisabled}
                            data-testid="BTN_function_submit"
                        >
                            {submitting ? (initialData ? 'Updating...' : 'Submitting...') : (initialData ? 'Update' : 'Submit')}
                        </Button>
                    )}
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default FunctionForm;
